<template>
	<div
		class="message-sending"
		:class="{ sending: isSending, active: isUploading || isFailed }"
	>
		<slot />

		<div class="progress-container" v-if="isUploading">
			<ProgressBar :percent="progress" square />
		</div>
		<div class="failure" v-if="isFailed">
			<div class="failure-info" v-text="$t('chat_message_senderror_text')" />
			<div
				class="failure-retry"
				v-text="$t('chat_message_senderror_option_retry')"
				@click="onRetry"
			/>
		</div>
	</div>
</template>

<script setup>
import { computed, watch, ref, onMounted, onUnmounted } from 'vue'
import {
	calcTotalProgressForMessageUUID,
	MESSAGE_PROGRESS_UNSENT_RATIO,
} from '@/store/uploads'
import bus from '@/helpers/bus'
import { ProgressBar } from '@/components/common'
import { sendMessage, uploadMediaWithStore } from '@/api/message'
import { updateMessage } from '@/store'
import { createLogger } from '@/helpers/log'

const log = createLogger('MessageSendingState', true)
const props = defineProps({ message: Object })
const emit = defineEmits(['progressComplete'])

const progress = ref(0)
const isSending = computed(() => {
	return props.message?.isWaiting || props.message?.isSending || false
})
const isUploading = computed(() => {
	return isSending.value && props.message?.hasMedia
})
const isFailed = computed(
	() => (!isSending.value && props.message?.isFailed) || false
)

const calcProgress = async () => {
	const actualProgress =
		calcTotalProgressForMessageUUID(props.message?.uuid) || 0
	if (props.message.isSent) {
		progress.value = actualProgress
	} else {
		progress.value = Math.floor(actualProgress * MESSAGE_PROGRESS_UNSENT_RATIO)
	}
}

calcProgress()

const onRetry = () => {
	log('retrying failed message', props.message.uuid)
	updateMessage(props.message.uuid, { sendState: 'waiting' })
	// re-upload any failed media files
	const failedMedia =
		props.message.messagemedia?.filter((m) => m.isFailed) || []
	if (failedMedia.length) {
		failedMedia.forEach((media) => {
			log('re-uploading failed media', media, props.message.uuid)
			uploadMediaWithStore(media)
		})
	} else {
		log('no failed uploads so re-sending failed message', props.message.uuid)
		sendMessage(props.message)
	}
}

const stopWatch = watch(isUploading, (val) => {
	if (!val) {
		bus.$off('upload-change', calcProgress)
		stopWatch()
	}
})

onMounted(() => {
	calcProgress()
	if (isUploading.value) {
		bus.$on('upload-change', calcProgress)
	}
})

onUnmounted(() => {
	bus.$off('upload-change', calcProgress)
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
$pad: 4px;
$pad-neg: -$pad;

.message-sending {
	width: 100%;
	max-width: max-content;
	padding: 0;
	margin: 0;
	background-color: transparent;
	overflow: hidden;
	transition: all ease-in-out 300ms;
	transition-property: padding, margin, margin-bottom, background-color, opacity;
	&.sending {
		:slotted(> *) {
			opacity: 0.6;
		}
	}
	&.active {
		@include radius;
		padding: $pad;
		margin: $pad-neg;
		margin-bottom: 0;
		background-color: var(--neutral-100);
		.progress-container {
			margin-top: $pad;
			max-height: 4px;
		}
	}
	:slotted(> *) {
		opacity: 1;
		transition: opacity ease-in-out 300ms;
	}
	.progress-container {
		@include flex();
		@include fontFamily(800);
		flex-shrink: 0;
		margin: 0 $pad-neg $pad-neg $pad-neg;
		width: calc(100% + #{$pad} + #{$pad});
		font-size: 12px;
		color: var(--neutral-500);
		padding-top: 0;
		max-height: 0;
		overflow: hidden;
		transition: all ease-in-out 300ms;
		transition-property: margin-top, max-height;
	}
	.failure {
		@include flex(12px);
		@include fontFamily(800);
		font-size: 12px;
		padding: 6px 8px 2px 0;
		.failure-info {
			color: var(--red-400);
		}
		.failure-retry {
			color: var(--blue-400);
			cursor: pointer;
		}
	}
}
</style>
